import {
  TextField,
  Box,
  Checkbox,
  Switch,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Button as MuiButton,
  Typography,
} from '@mui/material';
import { ExternalLink, Plus, Save, Trash2 } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import apiReq from 'core/api';
import ConfluenceAuthModal from '../components/ConfluenceAuthModal';
import { confluenceAuthStorage } from '../utils/confluenceAuthStorage';
import { useSnackbar } from 'components/GlobalSnackbar';
import {
  SourceShell,
  Button as ShellButton,
  IconButton as ShellIconButton,
  EmptyState,
} from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import LegacyConfluencePage from './ConfluencePage.legacy';

function ConfluencePageNew() {
  const projectId = useSelector((state) => state.reducer.project.id);
  const isAdmin = useSelector((state) => state.reducer.isAdmin);

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  const [addOpen, setAddOpen] = useState(false);
  const [addUrl, setAddUrl] = useState('');
  const [addShared, setAddShared] = useState(true);
  const [adding, setAdding] = useState(false);
  const [togglingShareId, setTogglingShareId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const CONFLUENCE_TOKEN_KEY = 'confluence_access_token';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [savedToken, setSavedToken] = useState('');

  const showSnackbar = useSnackbar();
  const prevProjectIdRef = useRef(null);

  useEffect(() => {
    loadAuthStatus();
  }, []);

  const loadAuthStatus = () => {
    const authData = confluenceAuthStorage.get();
    setIsAuthenticated(authData.is_authenticated);
    try {
      const token = localStorage.getItem(CONFLUENCE_TOKEN_KEY);
      if (token && token.trim()) {
        setSavedToken(token.trim());
        if (!authData.is_authenticated) {
          confluenceAuthStorage.set({ is_authenticated: true });
          setIsAuthenticated(true);
        }
      }
    } catch (_) {
      void 0;
    }
  };

  const loadPages = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await apiReq(`confluence/pages?project_id=${projectId}`, 'GET');
      setPages(Array.isArray(data) ? data : []);
      setSelected(() => {
        const next = {};
        (Array.isArray(data) ? data : []).forEach((p) => {
          if (p.chosen) next[p.id] = true;
        });
        return next;
      });
    } catch (error) {
      showSnackbar({
        message: error.message || 'Ошибка при загрузке страниц',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, showSnackbar]);

  useEffect(() => {
    if (!isAuthenticated || !projectId) {
      setPages([]);
      setSelected({});
      prevProjectIdRef.current = null;
      return;
    }
    if (prevProjectIdRef.current !== projectId) {
      setPages([]);
      setSelected({});
      prevProjectIdRef.current = projectId;
    }
    loadPages();
  }, [isAuthenticated, projectId, loadPages]);

  const handleToggle = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddPage = async () => {
    if (!projectId || !addUrl?.trim()) return;
    setAdding(true);
    try {
      await apiReq('confluence/pages/add', 'POST', {
        url: addUrl.trim(),
        project_id: projectId,
        is_shared: addShared,
      });
      setAddOpen(false);
      setAddUrl('');
      setAddShared(true);
      showSnackbar({ message: 'Страница успешно добавлена', severity: 'success' });
      await loadPages();
    } catch (error) {
      showSnackbar({
        message: error.message || 'Ошибка при добавлении страницы',
        severity: 'error',
      });
    } finally {
      setAdding(false);
    }
  };

  const handleTogglePageShared = async (pageId) => {
    setTogglingShareId(pageId);
    try {
      const res = await apiReq('confluence/pages/toggle-shared', 'POST', { page_id: pageId });
      const nextShared = typeof res?.is_shared === 'boolean' ? res.is_shared : undefined;
      setPages((prev) =>
        prev.map((p) =>
          p.id === pageId && nextShared !== undefined ? { ...p, is_shared: nextShared } : p
        )
      );
      showSnackbar({ message: 'Настройка доступа обновлена', severity: 'success' });
    } catch (error) {
      showSnackbar({
        message: error.message || 'Не удалось изменить доступ',
        severity: 'error',
      });
    } finally {
      setTogglingShareId(null);
    }
  };

  const canDeleteConfluencePage = (p) => !!isAdmin || !!p.is_own || !!p.is_shared;

  const closeDeleteDialog = () => {
    if (deletingId) return;
    setDeleteTarget(null);
  };

  const handleConfirmDeletePage = async () => {
    if (!deleteTarget?.id) return;
    if (!canDeleteConfluencePage(deleteTarget)) {
      setDeleteTarget(null);
      showSnackbar({
        message: 'Недостаточно прав для удаления этой страницы',
        severity: 'warning',
      });
      return;
    }
    const pageId = deleteTarget.id;
    setDeletingId(pageId);
    try {
      await apiReq(`confluence/pages/delete?page_id=${encodeURIComponent(pageId)}`, 'DELETE');
      setPages((prev) => prev.filter((p) => p.id !== pageId));
      setSelected((prev) => {
        const next = { ...prev };
        delete next[pageId];
        return next;
      });
      setDeleteTarget(null);
      showSnackbar({ message: 'Страница удалена из проекта', severity: 'success' });
    } catch (error) {
      showSnackbar({
        message: error.message || 'Не удалось удалить страницу',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveSelected = async () => {
    if (!projectId) return;
    const ids = Object.keys(selected).filter((k) => selected[k]);
    setSaving(true);
    try {
      await apiReq('confluence/user/pages/save', 'POST', {
        project_id: projectId,
        pages_ids: ids,
      });
      showSnackbar({ message: 'Страницы успешно сохранены', severity: 'success' });
    } catch (error) {
      showSnackbar({
        message: error.message || 'Ошибка при сохранении страниц',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitToken = async (token) => {
    const trimmed = token.trim();
    if (!trimmed) return;
    try {
      await apiReq('user/update-confluence-token', 'POST', {
        confluence_token: trimmed,
      });
      localStorage.setItem(CONFLUENCE_TOKEN_KEY, trimmed);
      confluenceAuthStorage.set({ is_authenticated: true });
      setSavedToken(trimmed);
      loadAuthStatus();
      showSnackbar({ message: 'Токен успешно сохранён', severity: 'success' });
    } catch (error) {
      showSnackbar({
        message: error.message || 'Ошибка при авторизации',
        severity: 'error',
      });
    }
  };

  const handleLogout = async () => {
    try {
      await apiReq('user/update-confluence-token', 'POST', {
        confluence_token: '',
      });
      try {
        localStorage.removeItem(CONFLUENCE_TOKEN_KEY);
      } catch (_) {
        void 0;
      }
      confluenceAuthStorage.clear();
      setSavedToken('');
      setIsAuthenticated(false);
      showSnackbar({ message: 'Вы успешно вышли из аккаунта Confluence', severity: 'success' });
    } catch (error) {
      showSnackbar({
        message: error.message || 'Ошибка при выходе',
        severity: 'error',
      });
    }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = pages.length > 0 && pages.every((p) => selected[p.id]);
  const someChecked = pages.some((p) => selected[p.id]) && !allChecked;

  const toggleAll = (e) => {
    const checked = e.target.checked;
    const next = {};
    pages.forEach((p) => {
      next[p.id] = checked;
    });
    setSelected(next);
  };

  const tableHeader = (
    <thead>
      <tr>
        <th
          style={{
            width: 36,
            background: 'var(--shell-bg-subtle)',
            borderBottom: '1px solid var(--shell-border-subtle)',
            padding: '0 4px',
            height: 36,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Checkbox
            size="small"
            indeterminate={someChecked}
            checked={allChecked}
            onChange={toggleAll}
            sx={{ p: 0.5 }}
          />
        </th>
        {['Название', 'URL', 'Доступ', ''].map((label, i) => (
          <th
            key={i}
            style={{
              textAlign: i === 2 ? 'center' : i === 3 ? 'right' : 'left',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'var(--shell-text-tertiary)',
              background: 'var(--shell-bg-subtle)',
              borderBottom: '1px solid var(--shell-border-subtle)',
              padding: '0 12px',
              height: 36,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              width: i === 2 ? 140 : i === 3 ? 56 : undefined,
            }}
          >
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );

  const tableBody = (
    <tbody>
      {pages.length === 0 ? (
        <tr>
          <td colSpan={5} style={{ padding: 0, background: 'var(--shell-bg-surface)' }}>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 4,
                  color: 'var(--shell-text-tertiary)',
                }}
              >
                <CircularProgress size={16} sx={{ color: 'var(--shell-accent-primary)' }} />
                <span style={{ fontSize: 13 }}>Загрузка списка…</span>
              </Box>
            ) : (
              <EmptyState
                size="sm"
                title={!projectId ? 'Выберите проект' : 'Список пуст'}
                description={
                  projectId
                    ? 'Добавьте страницу Confluence через кнопку выше.'
                    : 'Оценки и источники привязаны к проекту.'
                }
              />
            )}
          </td>
        </tr>
      ) : (
        pages.map((p) => (
          <tr
            key={p.id}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--shell-bg-subtle)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            style={{ transition: 'background var(--shell-transition-fast)' }}
          >
            <td
              style={{
                padding: '0 4px',
                height: 40,
                borderBottom: '1px solid var(--shell-border-subtle)',
                width: 36,
              }}
            >
              <Checkbox
                size="small"
                checked={!!selected[p.id]}
                onChange={() => handleToggle(p.id)}
                sx={{ p: 0.5 }}
              />
            </td>
            <td
              style={{
                padding: '0 12px',
                height: 40,
                borderBottom: '1px solid var(--shell-border-subtle)',
                fontWeight: 500,
                color: 'var(--shell-text-primary)',
              }}
            >
              {p.title || p.name || p.id}
            </td>
            <td
              style={{
                padding: '0 12px',
                height: 40,
                borderBottom: '1px solid var(--shell-border-subtle)',
                fontSize: 12,
                color: 'var(--shell-text-secondary)',
                maxWidth: 0,
              }}
            >
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--shell-text-secondary)',
                  textDecoration: 'none',
                  borderBottom: '1px dashed var(--shell-border-default)',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.url}
                </span>
                <ExternalLink size={12} strokeWidth={1.75} />
              </a>
            </td>
            <td
              style={{
                padding: '0 12px',
                height: 40,
                borderBottom: '1px solid var(--shell-border-subtle)',
                textAlign: 'center',
                width: 140,
              }}
            >
              {p.is_own ? (
                <Switch
                  size="small"
                  checked={!!p.is_shared}
                  disabled={togglingShareId === p.id}
                  onChange={() => handleTogglePageShared(p.id)}
                />
              ) : (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 500,
                    borderRadius: 999,
                    background: p.is_shared
                      ? 'var(--shell-accent-subtle)'
                      : 'var(--shell-bg-subtle)',
                    color: p.is_shared
                      ? 'var(--shell-accent-pressed)'
                      : 'var(--shell-text-tertiary)',
                    border: '1px solid',
                    borderColor: p.is_shared
                      ? 'var(--shell-accent-primary)'
                      : 'var(--shell-border-subtle)',
                  }}
                >
                  {p.is_shared ? 'Открыт' : 'Закрыт'}
                </span>
              )}
            </td>
            <td
              style={{
                padding: '0 8px',
                height: 40,
                borderBottom: '1px solid var(--shell-border-subtle)',
                textAlign: 'right',
                width: 56,
              }}
            >
              {canDeleteConfluencePage(p) ? (
                <Tooltip title="Удалить из проекта">
                  <span>
                    <ShellIconButton
                      size="sm"
                      tone="danger"
                      label="Удалить"
                      disabled={!!deletingId}
                      onClick={() => setDeleteTarget(p)}
                      icon={<Trash2 size={14} strokeWidth={1.75} />}
                    />
                  </span>
                </Tooltip>
              ) : (
                <span style={{ color: 'var(--shell-text-tertiary)' }}>—</span>
              )}
            </td>
          </tr>
        ))
      )}
    </tbody>
  );

  return (
    <>
      <SourceShell
        title="Confluence"
        subtitle="Страницы базы знаний, подключённые к проекту"
        actions={
          <ShellButton
            variant="primary"
            leftIcon={<Plus size={14} strokeWidth={2} />}
            onClick={() => setAddOpen(true)}
            disabled={!isAuthenticated || !projectId || loading}
          >
            Добавить страницу
          </ShellButton>
        }
        connection={{
          status: isAuthenticated ? 'connected' : 'disconnected',
          label: 'Confluence',
          hint: isAuthenticated
            ? savedToken
              ? `Токен ***${savedToken.slice(-4)}`
              : undefined
            : undefined,
          onConnect: () => setIsModalOpen(true),
          onDisconnect: handleLogout,
          connectLabel: 'Подключить токен',
        }}
        selection={
          selectedIds.length > 0
            ? {
                count: selectedIds.length,
                actions: (
                  <>
                    <ShellButton variant="ghost" onClick={() => setSelected({})}>
                      Сбросить
                    </ShellButton>
                    <ShellButton
                      variant="primary"
                      leftIcon={<Save size={14} strokeWidth={1.75} />}
                      onClick={handleSaveSelected}
                      disabled={saving || loading || !projectId}
                    >
                      {saving ? 'Сохранение…' : 'Сохранить в контекст'}
                    </ShellButton>
                  </>
                ),
              }
            : undefined
        }
      >
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {!isAuthenticated ? (
            <EmptyState
              title="Подключите Confluence"
              description="Нажмите «Подключить токен» в полосе сверху, чтобы получить доступ к страницам."
            />
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                fontSize: 13,
                color: 'var(--shell-text-primary)',
              }}
            >
              {tableHeader}
              {tableBody}
            </table>
          )}
        </div>
      </SourceShell>

      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setAddShared(true);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Добавить ссылку на страницу Confluence</DialogTitle>
        <DialogContent>
          <TextField
            label="URL страницы"
            variant="outlined"
            fullWidth
            value={addUrl}
            onChange={(e) => setAddUrl(e.target.value)}
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            sx={{ mt: 2, ml: 0 }}
            control={
              <Switch
                checked={addShared}
                onChange={(e) => setAddShared(e.target.checked)}
                color="primary"
              />
            }
            label="Доступна всем участникам проекта"
          />
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setAddOpen(false);
              setAddShared(true);
            }}
          >
            Отмена
          </MuiButton>
          <MuiButton
            onClick={handleAddPage}
            variant="contained"
            disabled={adding || !addUrl.trim()}
          >
            {adding ? 'Добавление…' : 'Добавить'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Удалить страницу из проекта?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {deleteTarget
              ? `«${deleteTarget.title || deleteTarget.name || deleteTarget.url || deleteTarget.id}» будет отвязана от проекта.`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeDeleteDialog} disabled={!!deletingId}>
            Отмена
          </MuiButton>
          <MuiButton
            onClick={handleConfirmDeletePage}
            variant="contained"
            color="error"
            disabled={!!deletingId}
          >
            {deletingId ? 'Удаление…' : 'Удалить'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <ConfluenceAuthModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmitToken}
        isAuthenticated={isAuthenticated}
        savedToken={savedToken}
        onLogout={handleLogout}
      />
    </>
  );
}

function ConfluencePage() {
  if (!USE_NEW_SHELL) {
    return <LegacyConfluencePage />;
  }
  return <ConfluencePageNew />;
}

export default ConfluencePage;
